import type { BoxProps } from '@chakra-ui/react';
import { Box, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import type { PromoCode } from 'types/cart/PromoCode';
import type { ProductComparison } from 'types/cms/blocks';
import { useDebouncedCallback } from 'use-debounce';

import { ProductCTA } from '../ProductCTA';
import { ProductImage } from '../ProductImage';
import { ProductInfo } from '../ProductInfo';
import type { RowHeights } from '.';

interface CarouselItemProps {
  productsCount: number;
  rowHeadings: (string | undefined)[];
  productData: (string | undefined)[];
  product: ProductComparison['products'][0];
  rowProps: BoxProps;
  computedRowHeights?: RowHeights;
  getRowHeights: (rowHeights: RowHeights) => void;
  promoCode?: PromoCode;
  ctaType: ProductComparison['ctaType'];
  imageType: ProductComparison['imageType'];
}

export function CarouselItem({
  productsCount,
  rowHeadings,
  productData,
  product,
  rowProps,
  computedRowHeights,
  getRowHeights,
  promoCode,
  ctaType,
  imageType,
}: CarouselItemProps) {
  const [productInfoComputedHeight, setProductInfoComputedHeight] = useState<
    string | undefined
  >(undefined);
  const productInfoRef = useRef<HTMLDivElement | null>(null);
  const tableCellRefs = useRef<(HTMLDivElement | null)[]>([]);

  const debounceCalculateRowHeights = useDebouncedCallback(
    () => calculateRowHeights(),
    200,
  );

  const calculateRowHeights = useCallback(() => {
    if (
      productInfoRef.current &&
      tableCellRefs.current.length === productData.length
    ) {
      const productInfoHeight = productInfoRef.current.clientHeight;
      const heights = tableCellRefs.current.map((el) => el?.clientHeight);
      getRowHeights([productInfoHeight, ...heights]);
    }
  }, [getRowHeights, productData.length]);

  useEffect(() => {
    if (computedRowHeights && computedRowHeights.length) {
      setProductInfoComputedHeight(`${computedRowHeights[0]}px`);
    }
  }, [computedRowHeights]);

  useEffectOnce(() => {
    debounceCalculateRowHeights();

    const handleResize = () => {
      setProductInfoComputedHeight(undefined);
      debounceCalculateRowHeights();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  return (
    <Box key={product.slug}>
      <Stack gap="3xs">
        <ProductImage
          {...product}
          imageType={imageType}
          productsCount={productsCount}
        />
        <ProductInfo
          {...product}
          ref={productInfoRef}
          height={productInfoComputedHeight}
          promoCode={promoCode}
          ctaType={ctaType}
        />
      </Stack>
      <Box as="ul">
        {productData.map((item, cellIndex) => {
          const heading = rowHeadings[cellIndex];
          const computedCellHeight =
            computedRowHeights && computedRowHeights[cellIndex + 1]
              ? `${computedRowHeights[cellIndex + 1]}px`
              : undefined;

          return (
            <Box
              as="li"
              key={cellIndex}
              ref={(el) => {
                tableCellRefs.current[cellIndex] = el;
              }}
              height={computedCellHeight}
              py="3xs"
              display="flex"
              flexDir="column"
              gap="5xs"
              {...rowProps}
            >
              <Text as="h2" size="title3">
                {heading}
              </Text>
              <Text as="span" size="body3">
                {item}
              </Text>
            </Box>
          );
        })}
      </Box>
      {ctaType !== 'none' && (
        <ProductCTA
          as="div"
          pt="2xs"
          href={product.href}
          slug={product.slug}
          promoCode={promoCode}
          ctaType={ctaType}
          {...rowProps}
        />
      )}
    </Box>
  );
}
